/**
 * Standalone Monday.com API service for Cloudflare deployment
 * Replaces Monday SDK with direct API calls
 */

class MondayApiService {
  constructor() {
    // Use Cloudflare Pages Function proxy in both development and production
    // Development: Vite proxy -> Cloudflare function
    // Production: Direct to Cloudflare function
    this.apiUrl = '/api/monday/v2';
    this.fileApiUrl = '/api/monday/v2/file';
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    return this.token || import.meta.env.VITE_MONDAY_API_TOKEN;
  }

  async makeRequest(query, variables = {}) {
    const token = this.getToken();

    if (!token) {
      throw new Error('No API token available');
    }

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify({
          query,
          variables
        })
      });

      const data = await response.json();

      if (data.errors) {
        console.error('Monday API errors:', data.errors);
        const message = data.errors[0].message || 'API request failed';

        // Show special popup if daily limit has been exceeded
        if (typeof window !== "undefined" && typeof message === "string" && message.toLowerCase().includes("daily limit exceeded")) {
          // Keep text exactly as requested
          window.alert("Problem occured: Notifiy Matz");
        }

        throw new Error(message);
      }

      return data.data;
    } catch (error) {
      console.error('Monday API request failed:', error);

      // Also catch network-level errors that might surface the same message
      if (typeof window !== "undefined" && error && typeof error.message === "string" && error.message.toLowerCase().includes("daily limit exceeded")) {
        window.alert("Problem occured: Notifiy Matz");
      }

      throw error;
    }
  }

  // Get current user info
  async getCurrentUser() {
    const query = `
      query {
        me {
          id
          name
          email
        }
      }
    `;
    return this.makeRequest(query);
  }

  // Get boards
  async getBoards() {
    const query = `
      query {
        boards {
          id
          name
          description
          type
          columns {
            id
            title
            type
          }
        }
      }
    `;
    return this.makeRequest(query);
  }

  // Get board items with pagination
  async getBoardItems(boardId, limit = 100, page = 1) {
    const query = `
      query ($boardId: [Int!], $limit: Int, $page: Int) {
        boards (ids: $boardId) {
          id
          name
          items_page (limit: $limit, page: $page) {
            cursor
            items {
              id
              name
              created_at
              updated_at
              column_values {
                id
                text
                value
                type
              }
              subitems {
                id
                name
                column_values {
                  id
                  text
                  value
                  type
                }
              }
            }
          }
        }
      }
    `;
    return this.makeRequest(query, {
      boardId: [parseInt(boardId)],
      limit,
      page
    });
  }

  // Get all items from a board (handles pagination automatically)
  async getAllBoardItems(boardId) {
    const allItems = [];
    let page = 1;
    let hasMoreItems = true;

    while (hasMoreItems) {
      const response = await this.getBoardItems(boardId, 100, page);
      const board = response.boards[0];

      if (board && board.items_page && board.items_page.items.length > 0) {
        allItems.push(...board.items_page.items);
        page++;

        // If we got less than 100 items, we've reached the end
        if (board.items_page.items.length < 100) {
          hasMoreItems = false;
        }
      } else {
        hasMoreItems = false;
      }
    }

    return { boards: [{ items: allItems }] };
  }

  // Get board columns
  async getBoardColumns(boardId) {
    const query = `
      query ($boardId: [Int!]) {
        boards (ids: $boardId) {
          columns {
            id
            title
            type
            settings_str
          }
        }
      }
    `;
    return this.makeRequest(query, { boardId: [parseInt(boardId)] });
  }

  // Create item
  async createItem(boardId, itemName, columnValues = {}) {
    const query = `
      mutation ($boardId: Int!, $itemName: String!, $columnValues: JSON) {
        create_item (board_id: $boardId, item_name: $itemName, column_values: $columnValues) {
          id
          name
          column_values {
            id
            text
            value
          }
        }
      }
    `;
    return this.makeRequest(query, {
      boardId: parseInt(boardId),
      itemName,
      columnValues: JSON.stringify(columnValues)
    });
  }

  // Update item
  async updateItem(itemId, columnValues) {
    const query = `
      mutation ($itemId: Int!, $columnValues: JSON) {
        change_multiple_column_values (item_id: $itemId, column_values: $columnValues) {
          id
          name
          column_values {
            id
            text
            value
          }
        }
      }
    `;
    return this.makeRequest(query, {
      itemId: parseInt(itemId),
      columnValues: JSON.stringify(columnValues)
    });
  }

  // Update single column value
  async updateColumnValue(itemId, columnId, value) {
    const query = `
      mutation ($itemId: Int!, $columnId: String!, $value: JSON) {
        change_column_value (item_id: $itemId, column_id: $columnId, value: $value) {
          id
        }
      }
    `;
    return this.makeRequest(query, {
      itemId: parseInt(itemId),
      columnId,
      value: JSON.stringify(value)
    });
  }

  // Delete item
  async deleteItem(itemId) {
    const query = `
      mutation ($itemId: Int!) {
        delete_item (item_id: $itemId) {
          id
        }
      }
    `;
    return this.makeRequest(query, { itemId: parseInt(itemId) });
  }

  // Create subitem
  async createSubitem(itemId, subitemName, columnValues = {}) {
    const query = `
      mutation ($itemId: Int!, $subitemName: String!, $columnValues: JSON) {
        create_subitem (parent_item_id: $itemId, item_name: $subitemName, column_values: $columnValues) {
          id
          name
          column_values {
            id
            text
            value
          }
        }
      }
    `;
    return this.makeRequest(query, {
      itemId: parseInt(itemId),
      subitemName,
      columnValues: JSON.stringify(columnValues)
    });
  }

  // Upload file
  async uploadFile(itemId, columnId, file) {
    // Monday.com file upload requires specific multipart format and /v2/file endpoint
    const formData = new FormData();

    // Convert itemId to number if it's a string
    const itemIdNum = typeof itemId === 'string' ? parseInt(itemId, 10) : Number(itemId);

    if (isNaN(itemIdNum)) {
      throw new Error(`Invalid itemId: ${itemId}`);
    }

    // Monday.com specific format - different from standard GraphQL multipart
    const query = `
      mutation add_file($file: File!) {
        add_file_to_column (item_id: ${itemIdNum}, column_id: "${columnId}", file: $file) {
          id
        }
      }
    `;

    const variables = {
      file: null
    };

    // Monday.com expects these specific field names
    formData.append('query', query);
    formData.append('variables', JSON.stringify(variables));
    formData.append('map', JSON.stringify({ 'file': ['variables.file'] }));
    formData.append('file', file);

    const token = this.getToken();

    if (!token) {
      throw new Error('No API token available');
    }

    try {
      // Must use the /v2/file endpoint for file uploads
      const response = await fetch(this.fileApiUrl, {
        method: 'POST',
        headers: {
          'Authorization': token,
          // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload response error:', response.status, errorText);
        throw new Error(`Upload failed with status ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('Monday API errors:', data.errors);
        throw new Error(data.errors[0].message || 'File upload failed');
      }

      return data.data;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  // Get users
  async getUsers() {
    const query = `
      query {
        users {
          id
          name
          email
        }
      }
    `;
    return this.makeRequest(query);
  }

  // Get assets by IDs
  async getAssets(assetIds) {
    if (!assetIds || assetIds.length === 0) {
      return { assets: [] };
    }
    
    const query = `
      query ($assetIds: [ID!]!) {
        assets(ids: $assetIds) {
          id
          public_url
          file_extension
          file_size
        }
      }
    `;
    return this.makeRequest(query, { assetIds });
  }

  // Get workspace/account info
  async getAccount() {
    const query = `
      query {
        account {
          id
          name
        }
      }
    `;
    return this.makeRequest(query);
  }
}

// Create singleton instance
const mondayApi = new MondayApiService();

export default mondayApi;
